import { getValueByPath } from "shared/app/VerificationTemplate/utils";
import { IncomeSourceProvider } from "shared/types";
import { toMoney } from "shared/utils";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import { IncomeField, ItemDocument } from "./types";
import {
  documentTotal,
  earliestDate,
  getItemDocument,
  inclusiveDaysBetween,
  latestDate,
  parseDate
} from "./utils";

type W2Form = {
  annual_gross_income_amount?: number | null;
  annual_net_income_amount?: number | null;
};

const getDocuments = (items: VerifiedItem[]) =>
  items
    .map(getItemDocument)
    .filter((document): document is ItemDocument => Boolean(document));

const formAnnualTotal = (form: W2Form, field: IncomeField) =>
  (field === "gross"
    ? form.annual_gross_income_amount
    : form.annual_net_income_amount) ?? 0;

const documentAnnualTotal = (document: ItemDocument, field: IncomeField) =>
  (document.income_sources ?? []).reduce(
    (sum, source) => sum + documentTotal(source, field),
    0
  );

const getAnnualTotal = (document: ItemDocument, field: IncomeField) =>
  (document.income_sources ?? []).reduce((total, source) => {
    const forms = getValueByPath<W2Form[] | undefined>(source, ["forms"]) ?? [];
    if (!forms.length) return total + documentTotal(source, field);

    return forms.reduce(
      (sum, form) => sum + formAnnualTotal(form, field),
      total
    );
  }, 0);

const inscribeW2Formula = (
  items: VerifiedItem[],
  field: IncomeField
): string | null => {
  const annualTotals = getDocuments(items).map((document) =>
    getAnnualTotal(document, field)
  );
  if (!annualTotals.length) return null;

  const formattedAnnualTotals = annualTotals
    .map((total) => toMoney(total))
    .join(" + ");

  return annualTotals.length > 1
    ? `((${formattedAnnualTotals})/${annualTotals.length})/12`
    : `${formattedAnnualTotals}/12`;
};

const ocrolusW2Formula = (
  items: VerifiedItem[],
  field: IncomeField
): string | null => {
  const documentFormulas = getDocuments(items).flatMap((document) => {
    const sources = document.income_sources ?? [];
    if (!sources.length) return [];

    const total = documentAnnualTotal(document, field);
    const start = earliestDate(
      sources.map((source) => parseDate(source.start_date))
    );
    const end = latestDate(sources.map((source) => parseDate(source.end_date)));
    const daysBetween = inclusiveDaysBetween(start, end);
    if (!total || !daysBetween) return [];

    return [`(${toMoney(total)}/${daysBetween} days)`];
  });
  if (!documentFormulas.length) return null;

  return documentFormulas.length > 1
    ? `(${documentFormulas.join("+")})*365/12`
    : `${documentFormulas[0]}*365/12`;
};

export const W2Tooltip = ({
  items,
  field,
  provider,
  displayedMonthly
}: {
  items: VerifiedItem[];
  field: IncomeField;
  provider: string | null;
  displayedMonthly: number;
}) => {
  const formula =
    provider === IncomeSourceProvider.Inscribe
      ? inscribeW2Formula(items, field)
      : ocrolusW2Formula(items, field);
  if (!formula) return null;

  return (
    <>
      Verified {field} monthly calculation
      <br />
      {formula} = {toMoney(displayedMonthly)}
    </>
  );
};
